import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule, DatePipe, JsonPipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ClientIntegrationDto } from '../../../services/integration.service';
import { environment } from '../../../../environments/environment';
import {
  TestingCapabilities,
  TestConfiguration,
  TestHistory,
  BulkTestRequest
} from '../../../models/security.models';

// Define local interface for testing capabilities
interface LocalTestingCapabilities {
  canTestCreate: boolean;
  canTestUpdate: boolean;
  canTestCancel: boolean;
  canTestPushInfo: boolean;
  canTestPushWeight: boolean;
  canTestPreAlert: boolean;
  canTestFullWorkflow: boolean;
  hasCredentials: boolean;
  allowedEndpoints: string[];
  message?: string;
}

@Component({
  selector: 'app-client-integration-testing',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DatePipe, JsonPipe],
  templateUrl: './client-integration-testing.component.html',
  styleUrls: ['./client-integration-testing.component.css']
})
export class ClientIntegrationTestingComponent implements OnInit, OnDestroy, OnChanges {
  @Input() clientId!: string;
  @Input() client?: ClientIntegrationDto;

  private destroy$ = new Subject<void>();
  
  // Make environment available in template
  environment = environment;

  // Component state
  loading = false;
  capabilities: LocalTestingCapabilities | null = null;
  activeTestTab = 'overview';
  testInProgress = false;
  showTestHistory = false;
  showTestConfigurations = false;
  showSaveConfigDialog = false;
  showCredentials = false;
  
  // Data properties
  testingCapabilities: any = null;
  testHistory: any[] = [];
  credentials: any = null;
  
  // Test forms
  createOrderForm!: FormGroup;
  updateOrderForm!: FormGroup;
  cancelOrderForm!: FormGroup;
  pushInfoForm!: FormGroup;
  pushWeightForm!: FormGroup;
  preAlertForm!: FormGroup;
  fullWorkflowForm!: FormGroup;
  bulkTestForm!: FormGroup;

  // Test results
  testResults: any[] = [];
  selectedResult: any = null;
  showResultModal = false;
  showResultDetails = false;
  currentTestResult: any = null;

  // Test configurations
  testConfigurations: TestConfiguration[] = [];
  saveConfigForm!: FormGroup;

  // Bulk testing
  bulkTestProgress = 0;
  bulkTestResults: any[] = [];
  showBulkResults = false;

  constructor(
    private fb: FormBuilder,
    private clientIntegrationService: ClientIntegrationService
  ) {}

  ngOnInit(): void {
    console.log('🚀 [ngOnInit] Initializing client integration testing component for client:', this.clientId);
    this.initializeForms();
    this.loadTestingCapabilities();
    this.loadTestHistory();
    this.loadTestConfigurations();
  }

  ngOnChanges(): void {
    // Reload testing capabilities if client changes or component is reactivated
    if (this.clientId) {
      console.log('🔄 [ngOnChanges] Client or input changed, reloading testing capabilities for:', this.clientId);
      this.loadTestingCapabilities();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.createOrderForm = this.fb.group({
      orderId: ['', Validators.required],
      customerName: ['', Validators.required],
      customerPhone: [''],
      deliveryAddress: ['', Validators.required],
      items: this.fb.array([])
    });

    this.updateOrderForm = this.fb.group({
      orderId: ['', Validators.required],
      status: ['', Validators.required],
      updateReason: ['']
    });

    this.cancelOrderForm = this.fb.group({
      orderId: ['', Validators.required],
      cancelReason: ['', Validators.required]
    });

    this.pushInfoForm = this.fb.group({
      orderId: ['', Validators.required],
      shopeeNo: [''],
      customerInfo: this.fb.group({
        name: [''],
        phone: [''],
        address: ['']
      })
    });

    this.pushWeightForm = this.fb.group({
      orderId: ['', Validators.required],
      weight: ['', [Validators.required, Validators.min(0)]]
    });

    this.preAlertForm = this.fb.group({
      customerName: ['', Validators.required],
      items: this.fb.array([])
    });

    this.fullWorkflowForm = this.fb.group({
      customerName: ['', Validators.required],
      customerPhone: [''],
      deliveryAddress: ['', Validators.required],
      items: this.fb.array([])
    });

    this.saveConfigForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.bulkTestForm = this.fb.group({
      numberOfOrders: [5, [Validators.required, Validators.min(1), Validators.max(10)]],
      numberOfParcelsPerOrder: [2, [Validators.required, Validators.min(1), Validators.max(20)]],
      testPrefix: ['BULK', [Validators.required, Validators.maxLength(10)]]
    });
  }

  // Load testing capabilities
  loadTestingCapabilities(): void {
    console.log('🔍 [loadTestingCapabilities] Starting testing capabilities check for client:', this.clientId);
    this.loading = true;
    
    // Use the enhanced testing capabilities endpoint that provides comprehensive credential status
    this.clientIntegrationService.getTestingCapabilities(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('📡 [loadTestingCapabilities] Testing capabilities response:', response);
          
          try {
            // Handle the structured testing capabilities response
            let hasCredentials = false;
            let isSuccess = false;
            let capabilities: any = null;
            
            // Parse ApiResponse format from testing capabilities endpoint
            if (response && typeof response === 'object') {
              if ('success' in response && response.success) {
                isSuccess = true;
                capabilities = response.data;

                // Enhanced credential detection logic - check multiple indicators
                // Check for the canTest flag in the response data (lowercase 'c')
                hasCredentials = capabilities?.canTest === true;

                // Also check the CanTest flag (uppercase 'C') for backward compatibility
                if (!hasCredentials) {
                  hasCredentials = capabilities?.CanTest === true;
                }

                // If not explicitly set, check hasCredentials flag
                if (!hasCredentials) {
                  hasCredentials = capabilities?.hasCredentials === true;
                }

                // If still not set, check for credential indicators
                if (!hasCredentials && capabilities) {
                  // Check all the credential type flags
                  const hasApiCredentials = capabilities.hasApiCredentials === true;
                  const hasInboundCredentials = capabilities.hasInboundCredentials === true;
                  const hasOutboundCredentials = capabilities.hasOutboundCredentials === true;

                  // Check if any credential type is available
                  hasCredentials = hasApiCredentials || hasInboundCredentials || hasOutboundCredentials;

                  if (hasCredentials) {
                    console.log('🔄 [loadTestingCapabilities] Testing available based on credential flags');
                  }
                }
                
                console.log('🔍 [loadTestingCapabilities] Capabilities data:', {
                  canTest: capabilities?.canTest,
                  CanTest: capabilities?.CanTest,
                  hasCredentials: capabilities?.hasCredentials,
                  hasApiCredentials: capabilities?.hasApiCredentials,
                  hasInboundCredentials: capabilities?.hasInboundCredentials,
                  hasOutboundCredentials: capabilities?.hasOutboundCredentials,
                  overrideHasCredentials: hasCredentials,
                  status: capabilities?.status,
                  primaryCredentialType: capabilities?.PrimaryCredentialType,
                  primaryAccount: capabilities?.PrimaryAccount,
                  hasPartialCredentials: capabilities?.HasPartialCredentials,
                  credentialIssues: capabilities?.CredentialIssues,
                  allowedEndpointsCount: capabilities?.AllowedEndpoints?.length || 0
                });
              } else {
                console.log('⚠️ [loadTestingCapabilities] API responded with success=false:', response.message);
              }
            }
            
            // Set testing capabilities based on comprehensive credential check
            if (hasCredentials && capabilities) {
              console.log('✅ [loadTestingCapabilities] Client has valid credentials - testing available');
              console.log('🔑 [loadTestingCapabilities] Primary credential type:', capabilities.PrimaryCredentialType);
              
              const availableTests = capabilities.AvailableTests || {};
              
              // Check for test capability flags with both uppercase and lowercase variants
              const canTestCreate = availableTests?.CanTestCreate !== false || capabilities?.canTestCreate === true;
              const canTestUpdate = availableTests?.CanTestUpdate !== false || capabilities?.canTestUpdate === true;
              const canTestCancel = availableTests?.CanTestCancel !== false || capabilities?.canTestCancel === true;
              const canTestPushInfo = availableTests?.CanTestPushInfo !== false || capabilities?.canTestPushInfo === true;
              const canTestPushWeight = availableTests?.CanTestPushWeight !== false || capabilities?.canTestPushWeight === true;
              const canTestPreAlert = availableTests?.CanTestPreAlert !== false || capabilities?.canTestPreAlert === true;
              const canTestFullWorkflow = availableTests?.CanTestFullWorkflow !== false || capabilities?.canTestFullWorkflow === true;

              this.capabilities = {
                canTestCreate: canTestCreate,
                canTestUpdate: canTestUpdate,
                canTestCancel: canTestCancel,
                canTestPushInfo: canTestPushInfo,
                canTestPushWeight: canTestPushWeight,
                canTestPreAlert: canTestPreAlert,
                canTestFullWorkflow: canTestFullWorkflow,
                hasCredentials: true,
                allowedEndpoints: capabilities.AllowedEndpoints || capabilities.allowedEndpoints || [],
                message: capabilities.status === 'Fully Configured' ? 'Testing available - all capabilities enabled' : `Testing available using ${capabilities.PrimaryCredentialType || 'configured'} credentials`
              };
              
              this.generateDefaultTestData();
            } else if (isSuccess && capabilities && !hasCredentials) {
              console.log('ℹ️ [loadTestingCapabilities] Client exists but credentials insufficient for testing');
              console.log('🔍 [loadTestingCapabilities] Detailed credential analysis:', {
                hasPartialCredentials: capabilities.HasPartialCredentials,
                primaryCredentialType: capabilities.PrimaryCredentialType,
                primaryAccount: capabilities.PrimaryAccount,
                credentialIssues: capabilities.CredentialIssues,
                allowedEndpoints: capabilities.AllowedEndpoints
              });
              
              // Provide more specific messaging based on credential status
              let specificMessage = 'Client credentials required for testing.';
              
              if (capabilities.HasPartialCredentials) {
                const issues = capabilities.CredentialIssues || [];
                specificMessage = `Incomplete credentials detected: ${issues.join(', ')}. Please complete the credential configuration.`;
              } else if (capabilities.PrimaryCredentialType === 'None') {
                specificMessage = 'No credentials found. Please generate API credentials or configure integration credentials first.';
              } else {
                specificMessage = `Credentials found (${capabilities.PrimaryCredentialType}) but testing not available. Please check configuration.`;
              }
              
              this.capabilities = {
                canTestCreate: false,
                canTestUpdate: false,
                canTestCancel: false,
                canTestPushInfo: false,
                canTestPushWeight: false,
                canTestPreAlert: false,
                canTestFullWorkflow: false,
                hasCredentials: false,
                allowedEndpoints: [],
                message: specificMessage
              };
            } else {
              console.log('⚠️ [loadTestingCapabilities] Unable to determine testing capabilities');
              this.capabilities = {
                canTestCreate: false,
                canTestUpdate: false,
                canTestCancel: false,
                canTestPushInfo: false,
                canTestPushWeight: false,
                canTestPreAlert: false,
                canTestFullWorkflow: false,
                hasCredentials: false,
                allowedEndpoints: [],
                message: 'Unable to determine testing capabilities. Please check client configuration or try refreshing.'
              };
            }
            
            this.loading = false;
            console.log('✅ [loadTestingCapabilities] Final capabilities set:', this.capabilities);
            
          } catch (processingError) {
            console.error('❌ [loadTestingCapabilities] Error processing capabilities response:', processingError);
            this.capabilities = {
              canTestCreate: false,
              canTestUpdate: false,
              canTestCancel: false,
              canTestPushInfo: false,
              canTestPushWeight: false,
              canTestPreAlert: false,
              canTestFullWorkflow: false,
              hasCredentials: false,
              allowedEndpoints: [],
              message: 'Error processing testing capabilities. Please try again or contact support.'
            };
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('❌ [loadTestingCapabilities] Testing capabilities check failed:', error);
          console.error('❌ [loadTestingCapabilities] Error details:', {
            status: error.status,
            message: error.message,
            url: error.url,
            statusText: error.statusText
          });
          
          // Enhanced error handling with more specific messages
          let errorMessage = 'Testing unavailable - unable to check client credentials.';
          
          if (error.status === 404) {
            if (error.url?.includes('/testing-capabilities')) {
              errorMessage = 'Testing capabilities endpoint not found. The backend may need to be updated. Please contact support.';
              console.log('ℹ️ [loadTestingCapabilities] 404 response - testing capabilities endpoint not found');
            } else {
              errorMessage = 'Client not found. Please verify the client ID is correct.';
              console.log('ℹ️ [loadTestingCapabilities] 404 response - client not found');
            }
          } else if (error.status === 401 || error.status === 403) {
            errorMessage = 'Access denied. You do not have permission to view client testing capabilities.';
          } else if (error.status >= 500) {
            errorMessage = 'Server error occurred while checking testing capabilities. Please try again later.';
          } else if (error.status === 0) {
            errorMessage = 'Network connection error. Please check your internet connection.';
          } else if (error.message) {
            errorMessage = `Error checking testing capabilities: ${error.message}`;
          }
          
          // Always show the testing tab, but with error state
          this.capabilities = {
            canTestCreate: false,
            canTestUpdate: false,
            canTestCancel: false,
            canTestPushInfo: false,
            canTestPushWeight: false,
            canTestPreAlert: false,
            canTestFullWorkflow: false,
            hasCredentials: false,
            allowedEndpoints: [],
            message: errorMessage
          };
          
          this.loading = false;
          console.log('✅ [loadTestingCapabilities] Error state capabilities set:', this.capabilities);
        }
      });
  }

  // Load test history with graceful fallback
  loadTestHistory(): void {
    this.clientIntegrationService.getTestHistory(this.clientId, 10)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Test history loaded successfully:', response);
          // Handle both direct response and wrapped ApiResponse formats
          const items = response?.items || response || [];
          this.testHistory = Array.isArray(items) ? items : [];
          
          // Convert test history to simplified test results format for display
          this.testResults = this.testHistory.map(test => ({
            testType: test.testType || 'Unknown',
            success: test.success,
            responseTime: test.executionTime || 0,
            executedAt: test.executedAt,
            response: test.response,
            error: test.details && !test.success ? test.details : undefined
          }));
        },
        error: (error) => {
          console.log('ℹ️ Test history endpoint responded with expected empty result:', error.status);
          // Initialize empty test results - will be populated as tests are run
          this.testResults = [];
          this.testHistory = [];
        }
      });
  }

  // Load saved test configurations with graceful fallback
  loadTestConfigurations(): void {
    this.clientIntegrationService.getTestConfigurations(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Test configurations loaded successfully:', response);
          // Handle both direct array response and wrapped ApiResponse formats
          const configurations = response || [];
          this.testConfigurations = Array.isArray(configurations) ? configurations : [];
        },
        error: (error) => {
          console.log('ℹ️ Test configurations endpoint responded with expected empty result:', error.status);
          this.testConfigurations = []; // Set empty array
        }
      });
  }

  // Generate default test data
  generateDefaultTestData(): void {
    const timestamp = new Date().toISOString().replace(/[-T:\.Z]/g, '').substring(0, 14);
    const uniqueId = `TEST_${timestamp}_${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    const shopeeNo = `ILH_TEST_${timestamp}`;

    // Populate create order form
    this.createOrderForm.patchValue({
      orderId: uniqueId,
      customerName: 'Test Customer',
      customerPhone: '+601234567890',
      deliveryAddress: '123 Test Street, Kuala Lumpur, Malaysia'
    });

    // Populate update order form
    this.updateOrderForm.patchValue({
      orderId: uniqueId,
      status: 'shipped',
      updateReason: 'Test order update'
    });

    // Populate cancel order form
    this.cancelOrderForm.patchValue({
      orderId: uniqueId,
      cancelReason: 'Test cancellation'
    });

    // Populate push info form
    this.pushInfoForm.patchValue({
      orderId: uniqueId,
      shopeeNo: shopeeNo,
      customerInfo: {
        name: 'Test Customer',
        phone: '+601234567890',
        address: '123 Test Street, Kuala Lumpur, Malaysia'
      }
    });

    // Populate push weight form
    this.pushWeightForm.patchValue({
      orderId: uniqueId,
      weight: 1.5
    });

    // Populate pre-alert form
    this.preAlertForm.patchValue({
      customerName: 'Test Customer'
    });

    // Populate full workflow form
    this.fullWorkflowForm.patchValue({
      customerName: 'Test Customer',
      customerPhone: '+601234567890',
      deliveryAddress: '123 Test Street, Kuala Lumpur, Malaysia'
    });
  }

  // Test execution methods
  executeCreateOrderTest(): void {
    if (this.createOrderForm.valid) {
      this.testInProgress = true;
      const testData = this.createOrderForm.value;
      
      // Use the proper test method that connects to backend
      const testRequest = {
        numberOfOrders: 1,
        numberOfParcelsPerOrder: testData.items?.length || 1,
        testPrefix: 'WEB_TEST',
        testUniqueId: testData.orderId,
        testShopeeNo: `ILH_${testData.orderId}`,
        testCarrierTn: `CARRIER_${Date.now()}`,
        testCartonNo: `CARTON_${Date.now()}`,
        testCartonWeight: 1.5,
        testCartonLength: 20,
        testCartonWidth: 15,
        testCartonHeight: 10,
        testDestinationRegion: 'MY',
        testDestinationRegionName: 'Malaysia',
        testGoodsType: 1,
        testServiceCode: 'STANDARD',
        testTransportType: 1,
        testParcelList: [`PARCEL_${Date.now()}`]
      };
      
      this.clientIntegrationService.testCreateOrder(this.clientId, testRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.addTestResult('Create Order', true, response);
            this.testInProgress = false;
          },
          error: (error: any) => {
            this.addTestResult('Create Order', false, null, error.message);
            this.testInProgress = false;
          }
        });
    }
  }

  executeUpdateOrderTest(): void {
    if (this.updateOrderForm.valid) {
      this.testInProgress = true;
      const testData = this.updateOrderForm.value;
      
      // Use the proper test method that connects to backend
      const testRequest = {
        uniqueId: testData.orderId,
        shopeeNo: `ILH_TEST_${Date.now()}`,
        updatedCarrierTn: 'TEST_CARRIER_' + Date.now(),
        updatedCartonWeight: 1.5,
        updatedParcelList: ['TEST_PARCEL_1']
      };
      
      this.clientIntegrationService.testUpdateOrder(this.clientId, testRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.addTestResult('Update Order', true, response);
            this.testInProgress = false;
          },
          error: (error: any) => {
            this.addTestResult('Update Order', false, null, error.message);
            this.testInProgress = false;
          }
        });
    }
  }

  executeCancelOrderTest(): void {
    if (this.cancelOrderForm.valid) {
      this.testInProgress = true;
      const testData = this.cancelOrderForm.value;
      
      // Mock implementation since service method doesn't exist
      console.log('Executing cancel order test with data:', testData);
      setTimeout(() => {
        this.addTestResult('Cancel Order', true, { message: 'Cancel order test completed (mock)' });
        this.testInProgress = false;
      }, 1000);
    }
  }

  executePushInfoTest(): void {
    if (this.pushInfoForm.valid) {
      this.testInProgress = true;
      const testData = this.pushInfoForm.value;
      
      // Mock implementation since service method doesn't exist
      console.log('Executing push info test with data:', testData);
      setTimeout(() => {
        this.addTestResult('Push Info', true, { message: 'Push info test completed (mock)' });
        this.testInProgress = false;
      }, 1000);
    }
  }

  executePushWeightTest(): void {
    if (this.pushWeightForm.valid) {
      this.testInProgress = true;
      const testData = this.pushWeightForm.value;
      
      // Mock implementation since service method doesn't exist
      console.log('Executing push weight test with data:', testData);
      setTimeout(() => {
        this.addTestResult('Push Weight', true, { message: 'Push weight test completed (mock)' });
        this.testInProgress = false;
      }, 1000);
    }
  }

  executePreAlertTest(): void {
    if (this.preAlertForm.valid) {
      this.testInProgress = true;
      const testData = this.preAlertForm.value;
      
      // Mock implementation since service method doesn't exist
      console.log('Executing pre-alert test with data:', testData);
      setTimeout(() => {
        this.addTestResult('Pre Alert', true, { message: 'Pre-alert test completed (mock)' });
        this.testInProgress = false;
      }, 1000);
    }
  }

  executeFullWorkflowTest(): void {
    if (this.fullWorkflowForm.valid) {
      this.testInProgress = true;
      const testData = this.fullWorkflowForm.value;
      
      this.clientIntegrationService.executeFullWorkflowTest()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.addTestResult('Full Workflow', true, response);
            this.testInProgress = false;
          },
          error: (error: any) => {
            this.addTestResult('Full Workflow', false, null, error.message);
            this.testInProgress = false;
          }
        });
    }
  }

  // Utility methods
  addTestResult(testType: string, success: boolean, response?: any, error?: string): void {
    const result = {
      testType,
      success,
      responseTime: Math.floor(Math.random() * 1000) + 100, // Mock response time
      executedAt: new Date().toISOString(),
      response: response || null,
      error: error || null
    };
    
    this.testResults.unshift(result); // Add to beginning of array
  }

  // Configuration management
  saveTestConfiguration(): void {
    if (this.saveConfigForm.valid) {
      const configName = this.saveConfigForm.get('name')?.value;
      const configData = this.getFormDataForTestType(this.activeTestTab);
      
      const config = {
        name: configName,
        description: this.saveConfigForm.get('description')?.value || '',
        testType: this.activeTestTab as "create" | "update" | "cancel" | "pushInfo" | "pushWeight" | "preAlert" | "fullWorkflow",
        configuration: configData,
        clientId: this.clientId,
        createdBy: 'current-user' // This should come from auth service
      };

      this.clientIntegrationService.saveTestConfiguration(config)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.loadTestConfigurations();
            this.showSaveConfigDialog = false;
            this.saveConfigForm.reset();
          },
          error: (error) => {
            console.error('Error saving configuration:', error);
          }
        });
    }
  }

  loadTestConfiguration(config: TestConfiguration): void {
    const form = this.getFormForTestType(config.testType);
    if (form) {
      form.patchValue(config.configuration);
    }
  }

  deleteConfiguration(config: TestConfiguration): void {
    if (config.id && confirm(`Are you sure you want to delete the configuration "${config.name}"?`)) {
      this.clientIntegrationService.deleteTestConfiguration(config.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadTestConfigurations();
          },
          error: (error) => {
            console.error('Error deleting configuration:', error);
          }
        });
    }
  }

  private getFormDataForTestType(testType: string): any {
    switch (testType) {
      case 'create': return this.createOrderForm.value;
      case 'update': return this.updateOrderForm.value;
      case 'cancel': return this.cancelOrderForm.value;
      case 'pushInfo': return this.pushInfoForm.value;
      case 'pushWeight': return this.pushWeightForm.value;
      case 'preAlert': return this.preAlertForm.value;
      case 'fullWorkflow': return this.fullWorkflowForm.value;
      default: return {};
    }
  }

  private getFormForTestType(testType: string): FormGroup | null {
    switch (testType) {
      case 'create': return this.createOrderForm;
      case 'update': return this.updateOrderForm;
      case 'cancel': return this.cancelOrderForm;
      case 'pushInfo': return this.pushInfoForm;
      case 'pushWeight': return this.pushWeightForm;
      case 'preAlert': return this.preAlertForm;
      case 'fullWorkflow': return this.fullWorkflowForm;
      default: return null;
    }
  }

  // Export and utility methods
  exportTestResults(): void {
    const fromDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const toDate = new Date().toISOString().split('T')[0];
    
    this.clientIntegrationService.exportTestResults(this.clientId, fromDate, toDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `client-${this.clientId}-test-results-${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
        },
        error: (error) => {
          console.error('Error exporting test results:', error);
        }
      });
  }

  getTestStatusIcon(success: boolean): string {
    return success ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger';
  }

  getTestStatusText(success: boolean): string {
    return success ? 'Success' : 'Failed';
  }

  formatTestDuration(duration: number): string {
    return `${duration}ms`;
  }

  closeResultDetails(): void {
    this.showResultDetails = false;
    this.currentTestResult = null;
  }

  cancelSaveConfig(): void {
    this.showSaveConfigDialog = false;
    this.saveConfigForm.reset();
  }

  refreshData(): void {
    console.log('🔄 [refreshData] Refreshing all testing data for client:', this.clientId);
    
    // Reset capabilities to show loading state
    this.capabilities = null;
    this.loading = true;
    
    // Clear existing data
    this.testResults = [];
    this.testHistory = [];
    this.testConfigurations = [];
    
    // Reload all data
    this.loadTestingCapabilities();
    this.loadTestHistory();
    this.loadTestConfigurations();
    
    console.log('⚙️ [refreshData] Data refresh initiated');
  }

  // Specific method to reload just credential status
  reloadCredentials(): void {
    console.log('🔄 [reloadCredentials] Reloading credentials for client:', this.clientId);
    
    // Reset capabilities and show loading
    this.capabilities = null;
    this.loading = true;
    
    // Only reload testing capabilities
    this.loadTestingCapabilities();
    
    console.log('⚙️ [reloadCredentials] Credential reload initiated');
  }

  // Quick test methods for simplified UI
  quickTestCreate(): void {
    this.generateDefaultTestData();
    this.executeCreateOrderTest();
  }

  quickTestUpdate(): void {
    this.generateDefaultTestData();
    this.executeUpdateOrderTest();
  }

  quickTestWorkflow(): void {
    this.executeFullWorkflowTest();
  }

  viewTestDetails(result: any): void {
    this.selectedResult = result;
    this.showResultModal = true;
  }

  closeResultModal(): void {
    this.showResultModal = false;
    this.selectedResult = null;
  }

  // Debug helper method to show detailed client information
  showDebugInfo(): void {
    console.group('🔍 [DEBUG] Client Integration Testing Debug Info');
    console.log('Client ID:', this.clientId);
    console.log('Client Object:', this.client);
    console.log('Current Capabilities:', this.capabilities);
    console.log('Loading State:', this.loading);
    console.log('Test Results Count:', this.testResults.length);
    console.log('Test History Count:', this.testHistory.length);
    console.log('Test Configurations Count:', this.testConfigurations.length);
    console.log('API Base URL:', `${environment.apiUrl || 'undefined'}`);
    console.log('Expected Endpoint:', `${environment.apiUrl || 'undefined'}/ClientIntegration/${this.clientId}/testing-capabilities`);
    console.groupEnd();
    
    // Also log current environment for debugging
    console.log('🌍 [DEBUG] Current Environment:', {
      isProduction: environment.isProduction || false,
      apiUrl: environment.apiUrl || 'undefined'
    });
  }

  // Force refresh capabilities - can be called by parent component
  forceRefreshCapabilities(): void {
    console.log('🔄 [forceRefreshCapabilities] Forcing refresh of testing capabilities');
    this.capabilities = null;
    this.loading = true;
    this.loadTestingCapabilities();
  }

  executeBulkTest(): void {
    if (!this.bulkTestForm.valid) {
      console.error('Bulk test form is invalid');
      return;
    }

    this.loading = true;
    const formValue = this.bulkTestForm.value;
    
    console.log('Starting bulk test with parameters:', formValue);

    const bulkRequest = {
      numberOfOrders: formValue.numberOfOrders,
      numberOfParcelsPerOrder: formValue.numberOfParcelsPerOrder,
      testPrefix: formValue.testPrefix
    };

    // Get client credentials first
    this.clientIntegrationService.getClientAuthToken(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tokenResponse) => {
          console.log('Client credentials retrieved for bulk test:', tokenResponse);
          
          // Now execute the bulk test
          this.clientIntegrationService.testBulkCreateOrders(this.clientId, bulkRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                console.log('Bulk test completed successfully:', response);
                this.loading = false;
                
                // Handle both direct response format and ApiResponse wrapper
                const isSuccess = response?.success !== false; // Default to true unless explicitly false
                const data = response?.data || response;
                const responseTime = data?.executionTime || 0;
                
                // Add to test results
                const result = {
                  testType: 'Bulk Create Orders',
                  success: isSuccess,
                  responseTime: responseTime,
                  executedAt: new Date().toISOString(),
                  response: response,
                  error: isSuccess ? undefined : (response?.message || 'Unknown error')
                };
                
                this.testResults.unshift(result);
                this.selectedResult = result;
                
                // Show success message
                if (isSuccess) {
                  const successfulOrders = data?.successfulOrders || formValue.numberOfOrders;
                  const totalOrders = data?.totalOrders || formValue.numberOfOrders;
                  console.log(`✅ Bulk test completed! Created ${successfulOrders}/${totalOrders} orders`);
                  
                  const failedOrders = data?.failedOrders || 0;
                  if (failedOrders > 0) {
                    console.warn(`⚠️ ${failedOrders} orders failed`, data?.errors);
                  }
                } else {
                  console.error('❌ Bulk test failed:', response?.message);
                }
              },
              error: (error) => {
                console.error('Bulk test failed:', error);
                this.loading = false;
                
                // Add error to test results
                const result = {
                  testType: 'Bulk Create Orders',
                  success: false,
                  responseTime: 0,
                  executedAt: new Date().toISOString(),
                  response: null,
                  error: `Bulk test failed: ${error.message || error}`
                };
                
                this.testResults.unshift(result);
                this.selectedResult = result;
              }
            });
        },
        error: (error) => {
          console.error('Failed to retrieve client credentials for bulk test:', error);
          this.loading = false;
          
          // Add credential error to test results
          const result = {
            testType: 'Bulk Create Orders',
            success: false,
            responseTime: 0,
            executedAt: new Date().toISOString(),
            response: null,
            error: `Credential retrieval failed: ${error.message || error}`
          };
          
          this.testResults.unshift(result);
          this.selectedResult = result;
        }
      });
  }

  // Credential management methods
  generateCredentials(): void {
    this.loading = true;
    // Mock credential generation
    setTimeout(() => {
      this.credentials = {
        apiAccount: `client-${this.clientId}`,
        apiSecretKey: 'mock-secret-key-' + Date.now(),
        isActive: true,
        createdAtUtc: new Date().toISOString(),
        allowedEndpoints: ['create-order', 'update-order', 'get-status'],
        exampleJwtHeader: {
          alg: 'HS256',
          typ: 'JWT'
        }
      };
      this.loading = false;
      console.log('Generated credentials:', this.credentials);
    }, 1000);
  }

  regenerateCredentials(): void {
    this.generateCredentials();
  }

}




